import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export function ComboboxWithInput({ 
  options, 
  value, 
  onValueChange, 
  placeholder = "Select or type...", 
  searchPlaceholder = "Search or type custom value...",
  className 
}) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(value || "")

  React.useEffect(() => {
    setInputValue(value || "")
  }, [value])

  const handleInputChange = (newValue) => {
    setInputValue(newValue)
  }

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault()
      onValueChange(inputValue.trim())
      setOpen(false)
    }
  }

  const handleSelect = (selectedValue) => {
    onValueChange(selectedValue === value ? "" : selectedValue)
    setInputValue(selectedValue === value ? "" : selectedValue)
    setOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onValueChange("")
    setInputValue("")
  }

  const displayValue = value || ""
  const isCustomValue = value && !options.find(opt => opt.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full h-12 justify-between bg-white border-slate-200 text-slate-900 font-normal",
            !displayValue && "text-slate-500",
            className
          )}
        >
          <span className="truncate">
            {displayValue || placeholder}
            {isCustomValue && <span className="ml-1 text-xs text-blue-500">(custom)</span>}
          </span>
          <div className="flex items-center gap-1">
            {displayValue && (
              <X 
                className="h-4 w-4 opacity-50 hover:opacity-100" 
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0 bg-white border-slate-200" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder} 
            className="h-10"
            value={inputValue}
            onValueChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
          />
          <CommandList>
            {inputValue && !options.find(opt => opt.value.toLowerCase() === inputValue.toLowerCase()) && (
              <CommandItem
                value={inputValue}
                onSelect={() => handleSelect(inputValue)}
                className="cursor-pointer border-b border-slate-100 bg-blue-50"
              >
                <span className="text-blue-600">Use "{inputValue}"</span>
              </CommandItem>
            )}
            {options.length === 0 && !inputValue && (
              <CommandEmpty>No options available. Type to add custom value.</CommandEmpty>
            )}
            <CommandGroup>
              {options
                .filter(opt => !inputValue || opt.label.toLowerCase().includes(inputValue.toLowerCase()))
                .map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
